import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Flame, Radio, MessagesSquare, Gift, Trophy, ShoppingBag, Heart, Bookmark,
  Tag, PlayCircle, BarChart3, Store, Receipt, RefreshCw, Package, Boxes, ShoppingCart,
  Wallet, LineChart, Grid3x3, Users, Smartphone, ChevronRight, LogOut, Moon, Sun, Bell, Truck, Info, Mail, HelpCircle, Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Logo } from "./Logo";

const SECTIONS = [
  {
    items: [
      { label: "Discover", icon: Flame, path: "/" },
      { label: "Stores", icon: Store, path: "/stores" },
      { label: "Go Live", icon: Radio, path: "/sell", primary: true },
      { label: "Messages", icon: MessagesSquare, path: "/messages" },
      { label: "Notifications", icon: Bell, path: "/notifications" },
      { label: "Refer & Win", icon: Gift, path: "/invite" },
      { label: "About", icon: Info, path: "/about" },
      { label: "Help & FAQ", icon: HelpCircle, path: "/help" },
      { label: "Contact", icon: Mail, path: "/contact" },
    ],
  },
  {
    title: "Shopping",
    items: [
      { label: "Cart", icon: ShoppingCart, path: "/cart" },
      { label: "Purchases", icon: ShoppingBag, path: "/purchases" },
      { label: "Following", icon: Heart, path: "/following" },
      { label: "Saved", icon: Bookmark, path: "/saved" },
      { label: "Offers", icon: Tag, path: "/offers" },
    ],
  },
  {
    title: "Seller Dashboard",
    items: [
      { label: "My Shows", icon: BarChart3, path: "/my-shows" },
      { label: "Store Profile", icon: Store, path: "/store-profile" },
      { label: "Orders", icon: Receipt, path: "/seller-orders" },
      { label: "Returns", icon: RefreshCw, path: "/returns" },
      { label: "Inventory", icon: Boxes, path: "/inventory" },
      { label: "Shipments", icon: Package, path: "/shipments" },
      { label: "Shipping Settings", icon: Truck, path: "/shipping-settings" },
      { label: "Simulcast", icon: Radio, path: "/simulcast" },
      { label: "Finances", icon: Wallet, path: "/finances" },
      { label: "Insights", icon: LineChart, path: "/insights" },
    ],
  },
];

const ADMIN_ITEMS = [
  { label: "Admin Dashboard", icon: Grid3x3, path: "/admin" },
  { label: "Waitlist Admin", icon: Users, path: "/admin-waitlist" },
  { label: "Shipping", icon: Truck, path: "/admin-shipping" },
];

export function DesktopSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const { count: cartCount } = useCart();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let on = true;
    const poll = () => api.get("/notifications").then((d) => on && setUnread(d.unread)).catch(() => {});
    poll();
    const id = setInterval(poll, 20000);
    const onRead = () => setUnread(0);
    window.addEventListener("notif-read", onRead);
    return () => { on = false; clearInterval(id); window.removeEventListener("notif-read", onRead); };
  }, []);

  const isActive = (path) => (path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(path + "/"));

  const NavItem = ({ it }) => {
    const Icon = it.icon;
    const active = isActive(it.path);
    const showBadge = (it.path === "/notifications" && unread > 0) || (it.path === "/cart" && cartCount > 0);
    const badgeVal = it.path === "/cart" ? cartCount : unread;
    return (
      <button
        className={`ss-nav-item ${active ? "active" : ""}`}
        onClick={() => navigate(it.path)}
        data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span className={`ss-nav-ic ${it.primary ? "primary" : ""}`} style={{ position: "relative" }}>
          <Icon size={18} />
          {showBadge && <span className="notif-badge" data-testid={`${it.path === "/cart" ? "cart" : "notif"}-badge`}>{badgeVal}</span>}
        </span>
        <span className="ss-nav-label">{it.label}</span>
      </button>
    );
  };

  return (
    <aside className="ss-sidebar">
      <div className="ss-sidebar-brand" onClick={() => navigate("/")} style={{ cursor: "pointer" }} data-testid="sidebar-brand">
        <Logo height={112} />
      </div>

      <nav className="ss-nav">
        {SECTIONS.map((sec, i) => (
          <div className="ss-nav-section" key={i}>
            {sec.title && <div className="ss-nav-title">{sec.title}</div>}
            {sec.items.map((it) => <NavItem key={it.path} it={it} />)}
          </div>
        ))}

        <button className="ss-getapp" onClick={() => navigate("/download")} data-testid="sidebar-download">
          <Smartphone size={18} style={{ color: "var(--gold)" }} />
          <span style={{ flex: 1 }}>
            <span style={{ display: "block", fontWeight: 800, fontSize: 14 }}>Get the app</span>
            <span className="ss-muted" style={{ fontSize: 12 }}>iOS &amp; Android</span>
          </span>
          <ChevronRight size={16} className="ss-muted" />
        </button>

        {user?.is_admin && (
          <div className="ss-nav-section">
            <div className="ss-nav-title">Admin</div>
            {ADMIN_ITEMS.map((it) => <NavItem key={it.path} it={it} />)}
          </div>
        )}
      </nav>

      <div className="ss-sidebar-footer">
        <button className="ss-nav-item" onClick={toggle} data-testid="theme-toggle" style={{ marginBottom: 4 }}>
          <span className="ss-nav-ic">{mode === "light" ? <Moon size={16} /> : <Sun size={16} />}</span>
          <span className="ss-nav-label">{mode === "light" ? "Dark mode" : "Light mode"}</span>
        </button>
        <button className="ss-nav-item" onClick={() => navigate("/settings")} data-testid="sidebar-settings" style={{ marginBottom: 4 }}>
          <span className="ss-nav-ic"><SettingsIcon size={16} /></span>
          <span className="ss-nav-label">Account Settings</span>
        </button>
        <div className="ss-user-row" onClick={() => navigate("/profile")} data-testid="sidebar-profile">
          <img className="ss-avatar" src={user?.avatar} alt={user?.name} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "Account"}</span>
            <span className="ss-muted" style={{ fontSize: 12 }}>View profile</span>
          </span>
        </div>
        <button className="ss-logout" onClick={async () => { await logout(); navigate("/landing"); }} data-testid="sidebar-logout">
          <LogOut size={18} /> Sign Out
        </button>
      </div>
    </aside>
  );
}
